import React from 'react';
import { Col } from 'antd';
import { isNil } from 'lodash';

import { getLocation } from 'utils/locationUtils';
import { getWorkerTypeLabel } from 'utils/workerUtils';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getWorkerInfoFile } from 'redux/reducers/workerSlice';
import { WorkerType } from '../types';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  worker: WorkerType;
  workerFileKeyInfo?: ResourceDownloadResponse;
};

export default function WorkerGeneralInfo(props: Props) {
  const { worker, workerFileKeyInfo } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const { workerInfo, workerKey, assignmentsCount, location, workerType } =
    worker;

  const homeAddress = getLocation(
    location?.country,
    location?.address,
    location?.state || location?.internationalStateName,
    location?.city || location?.internationalCityName,
    location?.zipCode,
  );

  const dispatch = useAppDispatch();

  const handleDownloadWorkerFile = async () => {
    if (!worker.id) return;

    const result = await dispatch(
      getWorkerInfoFile({
        workerId: worker.id,
        isAdminRole: isAdminRole as boolean,
      }),
    );

    if (getWorkerInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <>
      {!isNil(workerKey) && (
        <Col xs={24} md={12}>
          <div className="title">Worker ID:</div>
          <div>{workerKey}</div>
        </Col>
      )}

      {!isNil(assignmentsCount) && (
        <Col xs={24} md={12}>
          <div className="title">Number of assignments:</div>
          <div>{assignmentsCount}</div>
        </Col>
      )}

      {!isNil(workerInfo?.email) && (
        <Col xs={24} md={12}>
          <div className="title">Email:</div>
          <div>{workerInfo?.email}</div>
        </Col>
      )}

      {!isNil(workerInfo?.phoneNumber) && (
        <Col xs={24} md={12}>
          <div className="title">Phone number:</div>
          <div>{workerInfo?.phoneNumber}</div>
        </Col>
      )}

      {!!homeAddress && (
        <Col xs={24} md={12}>
          <div className="title">Home address:</div>
          <div>{homeAddress}</div>
        </Col>
      )}

      {!isNil(workerType) && (
        <Col xs={24} md={12}>
          <div className="title">Worker type:</div>
          <div>{getWorkerTypeLabel(workerType)}</div>
        </Col>
      )}

      {!isNil(workerInfo?.taxId) && (
        <Col xs={24} md={12}>
          <div className="title">Tax ID:</div>
          <div>{workerInfo?.taxId}</div>
        </Col>
      )}

      {!!workerFileKeyInfo && (
        <Col xs={24} md={12}>
          <div className="title">W9:</div>
          <span className="link-imitation" onClick={handleDownloadWorkerFile}>
            {getFileNameWithoutExt(workerFileKeyInfo?.fileName)}
          </span>
        </Col>
      )}
    </>
  );
}
